import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';
import BaseModal from './BaseModal';
import {
  datetimeLocalToISOString,
  toDatetimeLocalValue
} from '../../utils/dateTime';

const BookMeetingModal = ({ isOpen, onClose, initialData = {} }) => {
  // Endret addMeeting til addActivity (som er den reelle funksjonen i konteksten)
  const { contacts, companies, addActivity, users } = useCRM();
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    date: toDatetimeLocalValue(new Date(new Date().getTime() + 24 * 60 * 60 * 1000)),
    end_date: toDatetimeLocalValue(new Date(new Date().getTime() + 25 * 60 * 60 * 1000)),
    platform: 'Teams',
    location: '',
    description: '',
    contact_id: initialData.contact_id || '',
    company_id: initialData.company_id || '',
    participants: [users[0]?.user_id]
  });

  const selectedCompany = useMemo(() => companies.find(c => c.company_id === formData.company_id), [formData.company_id, companies]);
  const selectedContact = useMemo(() => contacts.find(c => c.contact_id === formData.contact_id), [formData.contact_id, contacts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Generer en mock-link basert på valgt plattform for å simulere integrasjon
    let mockLink = '';
    if (formData.platform === 'Teams') mockLink = 'https://teams.microsoft.com/l/meetup-join/mock-12345';
    if (formData.platform === 'Zoom') mockLink = 'https://zoom.us/j/mock-98765';

    // Legger til i den globale CRM aktivitet-listen slik at det dukker opp i kalenderen
    addActivity({
      type: formData.platform === 'Physical' ? 'Møte' : 'Online møte',
      title: formData.title,
      description: formData.description + (mockLink ? `\n\nGenerert lenke: ${mockLink}` : ''),
      date: datetimeLocalToISOString(formData.date),
      end_date: datetimeLocalToISOString(formData.end_date),
      company_id: formData.company_id,
      contact_id: formData.contact_id,
      status: 'Planlagt',
      user_id: formData.participants[0],
      location: formData.platform === 'Physical' ? formData.location : formData.platform
    });

    onClose();
    setStep(1); // Nullstill til neste gang
    
    // Gir en liten visuell bekreftelse på at møtet er booket
    alert(`Møtet "${formData.title}" er booket og lagt til i kalenderen din!`);
  };

  const platforms = [
    { id: 'Teams', name: 'Microsoft Teams', icon: "🎥", color: 'bg-indigo-500' },
    { id: 'Zoom', name: 'Zoom Video', icon: "🎥", color: 'bg-blue-500' },
    { id: 'Physical', name: 'Fysisk Møte', icon: "📍", color: 'bg-slate-700' }
  ];

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Book Møte" size="lg">
      <div className="space-y-8">
        {/* Progress Bar */}
        <div className="flex items-center justify-between px-8 relative">
          <div className="absolute left-12 right-12 top-1/2 -translate-y-1/2 h-[2px] bg-slate-100"></div>
          {[1, 2].map((s) => (
            <div key={s} className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all border-2 ${step >= s ? 'bg-primary-600 border-primary-600 text-white shadow-lg shadow-primary-200' : 'bg-white border-slate-200 text-slate-400'}`}>
              {step > s ? <SafeIcon icon="✅" className="w-5 h-5" /> : s}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Møtetype & Plattform</label>
                <div className="grid grid-cols-3 gap-3">
                  {platforms.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, platform: p.id })}
                      className={`flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all ${formData.platform === p.id ? 'border-primary-600 bg-primary-50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${p.color}`}>
                        <SafeIcon icon={p.icon} className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 text-center leading-tight">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Møtetittel</label>
                  <input
                    required
                    placeholder="F.eks: Gjennomgang av tilbud"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Starttid</label>
                    <div className="relative">
                      <SafeIcon icon="📅" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="datetime-local"
                        required
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sluttid</label>
                    <div className="relative">
                      <SafeIcon icon="🕒" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="datetime-local"
                        required
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                disabled={!formData.title}
                onClick={() => setStep(2)}
                className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-50 transition-all uppercase tracking-widest"
              >
                Neste steg <SafeIcon icon="➡️" className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Selskap</label>
                  <select
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm"
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value, contact_id: '' })}
                  >
                    <option value="">Ingen valgt</option>
                    {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Kontaktperson</label>
                  <select
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm"
                    value={formData.contact_id}
                    onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                  >
                    <option value="">Ingen valgt</option>
                    {contacts
                      .filter(c => !formData.company_id || c.company_id === formData.company_id)
                      .map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              {formData.platform === 'Physical' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Møtested</label>
                  <div className="relative">
                    <SafeIcon icon="📍" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      placeholder="F.eks: Bedriftens lokaler"
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Agenda / Notater</label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-medium text-sm transition-all resize-none"
                  placeholder="Kort beskrivelse av hva møtet handler om..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl hover:bg-slate-200 transition-all uppercase tracking-widest"
                >
                  Tilbake
                </button>
                <button
                  type="submit"
                  className="flex-[2] py-4 bg-primary-600 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-primary-700 shadow-xl shadow-primary-100 transition-all uppercase tracking-widest"
                >
                  Book Møte <SafeIcon icon="✅" className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </form>

        {formData.platform !== 'Physical' && (
          <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-indigo-600 shadow-sm shrink-0">
              <SafeIcon icon="ℹ️" className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-black text-indigo-900 uppercase tracking-widest">Automatisk Link (Demo)</p>
              <p className="text-[10px] text-indigo-700 font-medium mt-1">
                I en fullversjon vil systemet koble seg til din {formData.platform} konto for å generere en ekte lenke. Nå legger vi ved en simulerings-lenke til kalenderen din.
              </p>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default BookMeetingModal;